/**
 * CSV Import Dialog Component
 *
 * Main dialog that orchestrates the CSV import flow including
 * file selection, upload progress, processing status, and results display.
 */

import { useEffect, useState } from 'react';
import { Upload, Loader2, AlertTriangle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CsvFileDropzone } from '@/components/csv-file-dropzone';
import { CsvImportProgress } from '@/components/csv-import-progress';
import { CsvImportResults } from '@/components/csv-import-results';
import { useCsvImportStateMachine, type CsvImportState } from '@/hooks/use-csv-import';

/**
 * Get dialog title based on current state
 */
function getDialogTitle(state: CsvImportState): string {
  switch (state) {
    case 'FILE_SELECTION':
    case 'FILE_SELECTED':
    case 'FILE_ERROR':
      return 'Import Users from CSV';
    case 'UPLOADING':
      return 'Uploading File';
    case 'UPLOAD_ERROR':
      return 'Upload Failed';
    case 'PROCESSING':
      return 'Processing Import';
    case 'PROCESSING_ERROR':
      return 'Import Error';
    case 'COMPLETED':
      return 'Import Complete';
    case 'CANCELLED':
      return 'Import Cancelled';
    default:
      return 'Import Users from CSV';
  }
}

/**
 * Get dialog description based on current state
 */
function getDialogDescription(state: CsvImportState): string {
  switch (state) {
    case 'FILE_SELECTION':
    case 'FILE_SELECTED':
      return 'Upload a CSV file to bulk import users awaiting migration.';
    case 'FILE_ERROR':
      return 'There was an issue with the selected file.';
    case 'UPLOADING':
      return 'Your file is being uploaded to the server.';
    case 'UPLOAD_ERROR':
      return 'The file upload failed. You can try again.';
    case 'PROCESSING':
      return 'Your import is being processed. This may take a few minutes.';
    case 'PROCESSING_ERROR':
      return 'There was an error processing your import.';
    case 'COMPLETED':
      return 'Your import has finished processing.';
    case 'CANCELLED':
      return 'The import was cancelled before completion.';
    default:
      return '';
  }
}

/**
 * CSV Import Dialog Props
 */
interface CsvImportDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when the dialog should close */
  onOpenChange: (open: boolean) => void;
}

/**
 * CSV Import Dialog Component
 */
export function CsvImportDialog({ open, onOpenChange }: CsvImportDialogProps) {
  const {
    state,
    selectedFile,
    uploadProgress,
    jobId,
    error,
    isIdempotent,
    jobStatus,
    isCancelling,
    isForceUnsticking,
    isUploading,
    open: openStateMachine,
    close,
    selectFile,
    setFileError,
    clearFileSelection,
    startUpload,
    retryUpload,
    retryPolling,
    cancelJob,
    forceUnstickJob,
  } = useCsvImportStateMachine();

  // State for force-unstick confirmation dialog
  const [showForceUnstickDialog, setShowForceUnstickDialog] = useState(false);
  const [forceUnstickReason, setForceUnstickReason] = useState('');

  // Sync external open state with internal state machine
  const handleOpenChange = (newOpen: boolean) => {
    if (newOpen && state === 'CLOSED') {
      openStateMachine();
    } else if (!newOpen && state !== 'CLOSED') {
      close();
    }
    onOpenChange(newOpen);
  };

  // Open state machine when dialog opens
  useEffect(() => {
    if (open && state === 'CLOSED') {
      openStateMachine();
    }
  }, [open, state, openStateMachine]);

  // Determine if dialog can be dismissed by clicking outside
  const canDismiss =
    !['UPLOADING', 'PROCESSING'].includes(state) && !isCancelling && !isForceUnsticking;

  // Handler for force-unstick confirmation
  const handleForceUnstick = async () => {
    try {
      await forceUnstickJob(forceUnstickReason || undefined);
    } catch {
      // Error is handled by the hook
    } finally {
      setShowForceUnstickDialog(false);
      setForceUnstickReason('');
    }
  };

  // Check if we're in a terminal state
  const isTerminal = ['COMPLETED', 'CANCELLED', 'PROCESSING_ERROR'].includes(state);

  // Get job progress for display
  const jobProgress = jobStatus?.progress;
  const failures = jobStatus?.failures;

  return (
    <Dialog open={open} onOpenChange={canDismiss ? handleOpenChange : undefined}>
      <DialogContent
        className="sm:max-w-[600px]"
        onPointerDownOutside={e => !canDismiss && e.preventDefault()}
        onEscapeKeyDown={e => !canDismiss && e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>{getDialogTitle(state)}</DialogTitle>
          <DialogDescription>{getDialogDescription(state)}</DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {/* File Selection States */}
          {['FILE_SELECTION', 'FILE_SELECTED', 'FILE_ERROR'].includes(state) && (
            <CsvFileDropzone
              onFileAccepted={selectFile}
              onFileError={setFileError}
              selectedFile={selectedFile}
              onClearFile={clearFileSelection}
              disabled={isUploading}
              error={state === 'FILE_ERROR' ? error : null}
            />
          )}

          {/* Uploading State */}
          {state === 'UPLOADING' && (
            <CsvImportProgress phase="uploading" uploadProgress={uploadProgress} />
          )}

          {/* Upload Error State */}
          {state === 'UPLOAD_ERROR' && (
            <CsvImportProgress phase="uploading" uploadProgress={uploadProgress} error={error} />
          )}

          {/* Processing State */}
          {state === 'PROCESSING' && (
            <CsvImportProgress
              phase="processing"
              jobProgress={jobProgress}
              jobStatus={jobStatus?.status}
              isIdempotent={isIdempotent}
            />
          )}

          {/* Processing Error State */}
          {state === 'PROCESSING_ERROR' && (
            <CsvImportProgress
              phase="processing"
              jobProgress={jobProgress}
              jobStatus="failed"
              error={error}
              isIdempotent={isIdempotent}
            />
          )}

          {/* Completed State */}
          {state === 'COMPLETED' && jobProgress && (
            <CsvImportResults
              progress={jobProgress}
              status="completed"
              failures={failures}
              wasCancelled={false}
            />
          )}

          {/* Cancelled State */}
          {state === 'CANCELLED' && jobProgress && (
            <CsvImportResults
              progress={jobProgress}
              status="cancelled"
              failures={failures}
              wasCancelled={true}
            />
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {/* File Selection Footer */}
          {['FILE_SELECTION', 'FILE_ERROR'].includes(state) && (
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
          )}

          {state === 'FILE_SELECTED' && (
            <>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={startUpload} disabled={isUploading}>
                <Upload className="mr-2 h-4 w-4" />
                Start Import
              </Button>
            </>
          )}

          {/* Uploading Footer */}
          {state === 'UPLOADING' && (
            <Button type="button" variant="outline" disabled>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading...
            </Button>
          )}

          {/* Upload Error Footer */}
          {state === 'UPLOAD_ERROR' && (
            <>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Cancel
              </Button>
              <Button type="button" onClick={retryUpload}>
                Try Again
              </Button>
            </>
          )}

          {/* Processing Footer */}
          {state === 'PROCESSING' && (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowForceUnstickDialog(true)}
                disabled={isCancelling || isForceUnsticking}
              >
                {isForceUnsticking ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Force Unsticking...
                  </>
                ) : (
                  <>
                    <AlertTriangle className="mr-2 h-4 w-4" />
                    Force Unstick
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={cancelJob}
                disabled={isCancelling || isForceUnsticking}
              >
                {isCancelling ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Cancel Import'
                )}
              </Button>
            </div>
          )}

          {/* Processing Error Footer */}
          {state === 'PROCESSING_ERROR' && (
            <>
              <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                Close
              </Button>
              <Button type="button" onClick={retryPolling}>
                Retry
              </Button>
            </>
          )}

          {/* Terminal State Footer */}
          {isTerminal && state !== 'PROCESSING_ERROR' && (
            <Button type="button" onClick={() => handleOpenChange(false)}>
              Done
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Force Unstick Confirmation Dialog */}
      <AlertDialog open={showForceUnstickDialog} onOpenChange={setShowForceUnstickDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              Force Unstick Job
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                This action will mark the job as failed and remove it from the processing queue. Use
                this only if the job appears to be stuck and is not making progress.
              </p>
              <p className="text-sm font-medium">
                Job ID: <code className="bg-muted px-1 py-0.5 rounded">{jobId}</code>
              </p>
              <div className="pt-2">
                <Label htmlFor="force-unstick-reason" className="text-sm font-medium">
                  Reason (optional)
                </Label>
                <Input
                  id="force-unstick-reason"
                  placeholder="e.g., Job stuck for over 30 minutes"
                  value={forceUnstickReason}
                  onChange={e => setForceUnstickReason(e.target.value)}
                  className="mt-1.5"
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isForceUnsticking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleForceUnstick}
              disabled={isForceUnsticking}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              {isForceUnsticking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Force Unsticking...
                </>
              ) : (
                'Force Unstick'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
